<template>
  <DataTable
    :value="examples"
    dataKey="id"
    v-model:editingRows="editing"
    v-model:expandedRows="expandedRows"
    @row-edit-save="onRowEditSave"
    editMode="row"
    scrollHeight="100%"
    scrollable
    style="height: 100%"
  >
    <Column expander style="width: 5rem" />
    <Column field="task" header="Task">
      <template #editor="{ data, field }">
        <Textarea v-model="data[field]" />
      </template>
    </Column>
    <Column field="rule" header="Rule">
      <template #editor="{ data, field }">
        <Textarea
          :model-value="data[field]"
          @update:model-value="onRuleChange(data, $event)"
          fluid
        />
      </template>
    </Column>
    <Column field="valid" header="Valid">
      <template #body="{ data }">
        <div class="flex align-items-center gap-2">
          <i
            :class="{
              'pi pi-check-circle text-green-500': data.valid === 'valid',
              'pi pi-times-circle text-red-500': data.valid === 'invalid',
              'pi pi-clock text-orange-500': data.valid === 'pending',
            }"
          ></i>
        </div>
      </template>
    </Column>

    <Column field="explanation" header="Explanation">
      <template #editor="{ data, field }">
        <Textarea v-model="data[field]" fluid />
      </template>
    </Column>
    <Column field="use" header="Use">
      <template #body="{ data }">
        <Checkbox v-model="data.use" :binary="true" disabled />
      </template>
      <template #editor="{ data }">
        <Checkbox v-model="data.use" :binary="true" />
      </template>
    </Column>
    <Column
      :rowEditor="true"
      style="width: 10%; min-width: 8rem"
      bodyStyle="text-align:center"
    ></Column>
    <Column style="width: 5%; min-width: 4rem" bodyStyle="text-align:center">
      <template #body="{ data }">
        <Button
          v-if="isEditing(data)"
          icon="pi pi-trash"
          severity="danger"
          size="small"
          @click="onRowDelete(data)"
        />
      </template>
    </Column>

    <template #expansion="{ data }">
      <div class="p-3">
        <h5>
          Error Details of parsing the rules using the current LARK grammar
        </h5>
        <p>{{ data.error }}</p>
      </div>
    </template>
  </DataTable>
</template>
<script setup lang="ts">
import { computed, ref, onMounted, watch } from "vue";
import { ChatApi, GrammarExample } from "@/src/model/ChatApi";
import { CHAT_API_TYPE } from "@/src/model/apiTypes";

const chatApi = new ChatApi();

const examples = ref<GrammarExample[]>([]);
const expandedRows = ref<GrammarExample[]>([]);
const editing = ref<GrammarExample[]>([]);

// Store original rule values when editing starts
const originalRules = ref<Map<number, string>>(new Map());

const getEmptyExample = (): GrammarExample => {
  return {
    id: -1,
    task: "New Example",
    rule: "the rule, seperate multiple rules with a semicolon",
    explanation: "Edit this entry to add it to the examples",
    use: true,
    valid: "pending",
    error: "save the rule first to see the response of the LARK parser",
  };
};

onMounted(async () => {
  examples.value = await chatApi.exec(CHAT_API_TYPE.ADMIN_LOAD_ALL_EXAMPLES);
  examples.value.push(getEmptyExample());
});

const isEditing = (data: GrammarExample): boolean => {
  return editing.value.some(item => item.id === data.id);
};

const onRuleChange = (data: GrammarExample, newValue: string) => {
  // Store original value if not already stored
  if (!originalRules.value.has(data.id)) {
    originalRules.value.set(data.id, data.rule);
  }

  // Update the rule
  data.rule = newValue;

  // Check if rule changed from original
  const originalRule = originalRules.value.get(data.id);
  if (originalRule && originalRule !== newValue) {
    data.valid = "pending";
    data.error = "Rule changed from original";
  }
};

const onRowEditSave = async (event: any) => {
  let { newData, index } = event;
  originalRules.value.delete(newData.id);

  examples.value[index] = await chatApi.exec(
    CHAT_API_TYPE.ADMIN_UPDATE_EXAMPLE,
    { example: newData }
  );

  if (newData.id !== examples.value[index].id) {
    examples.value.push(getEmptyExample());
  }
};

const onRowDelete = (data: GrammarExample) => {
  editing.value = editing.value.filter(item => item.id !== data.id);
  expandedRows.value = expandedRows.value.filter(item => item.id !== data.id);
  originalRules.value.delete(data.id);

  if (data.id !== -1) {
    const index = examples.value.findIndex(item => item.id === data.id);
    if (index !== -1) {
      examples.value.splice(index, 1);
    }
    chatApi.exec(CHAT_API_TYPE.ADMIN_DELETE_EXAMPLE, { id: data.id });
  }
};
</script>
<style scoped></style>
